import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { OtpInput, OtpSetup, EmployerSelect, LoginMarketing } from '@/components/auth';
import { BrandLogo } from '@/components/BrandLogo';
import { Seo } from '@/components/Seo';
import { SEO } from '@/lib/seo';

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <Seo title={SEO.loginTitle} description={SEO.loginDescription} path="/login" />
      <aside
        className="relative hidden min-h-screen w-full lg:block lg:w-[58%] xl:w-[60%]"
        aria-label="About Payroll-GPT"
      >
        <LoginMarketing />
      </aside>
      <main
        className="flex min-h-screen w-full flex-1 flex-col items-center justify-center bg-background px-4 py-10 lg:w-[42%] xl:w-[40%]"
        aria-label="Sign in"
      >
        <div className="mb-8 flex flex-col items-center text-center lg:hidden">
          <BrandLogo className="h-12 w-auto max-w-[16rem]" alt="Payroll Mauritius" />
          <h1 className="font-display mt-3 text-xl font-semibold tracking-tight">Payroll-GPT</h1>
          <p className="mt-1 text-sm text-muted-foreground">Private AI for Payroll Mauritius</p>
        </div>
        {children}
      </main>
    </div>
  );
}

export function LoginPage() {
  const {
    isAuthenticated,
    isLoading,
    loginStep,
    pendingUser,
    otpSetupData,
    employers,
    login,
    verifyOtp,
    startOtpSetup,
    verifyOtpSetup,
    selectEmployer,
    resetLoginState,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSetup, setIsLoadingSetup] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (loginStep === 'otp-setup' && !otpSetupData) {
      setIsLoadingSetup(true);
      startOtpSetup()
        .catch((err) => {
          toast({
            variant: 'destructive',
            title: 'Setup failed',
            description: err instanceof Error ? err.message : 'Failed to start OTP setup',
          });
          resetLoginState();
        })
        .finally(() => setIsLoadingSetup(false));
    }
  }, [loginStep, otpSetupData, startOtpSetup, resetLoginState, toast]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (isLoading || loginStep === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loginStep === 'otp' && pendingUser) {
    return (
      <AuthShell>
        <OtpInput email={pendingUser.email} onVerify={verifyOtp} onBack={resetLoginState} />
      </AuthShell>
    );
  }

  if (loginStep === 'otp-setup' && pendingUser) {
    if (isLoadingSetup || !otpSetupData) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <AuthShell>
        <OtpSetup
          otpauthUri={otpSetupData.otpauthUri}
          secret={otpSetupData.secret}
          email={pendingUser.email}
          onVerify={verifyOtpSetup}
          onBack={resetLoginState}
        />
      </AuthShell>
    );
  }

  if (loginStep === 'employer-select' && employers.length > 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
        <EmployerSelect employers={employers} onSelect={selectEmployer} onBack={resetLoginState} />
      </div>
    );
  }

  if (loginStep === 'no-access') {
    return (
      <AuthShell>
        <Card className="w-full max-w-md border-0 shadow-lg sm:border">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="font-display text-2xl font-bold">Payroll-GPT</CardTitle>
            <CardDescription>
              You do not have access to Payroll-GPT. Please contact support.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            If you believe this is an error, please reach out to your administrator or support team.
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await login(email, password);

      if (response.ok) {
        toast({
          title: 'Welcome back!',
          description: 'You have been successfully authenticated.',
        });
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Authentication failed',
        description: err instanceof Error ? err.message : 'Invalid email or password',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <Card className="w-full max-w-md border-0 shadow-none sm:border sm:shadow-sm">
        <CardHeader className="space-y-1 text-center sm:text-left">
          <CardTitle className="font-display text-2xl font-bold tracking-tight">
            <span className="lg:hidden">Sign in</span>
            <span className="hidden lg:inline">Sign in to Payroll-GPT</span>
          </CardTitle>
          <CardDescription>
            Enter your Payroll Mauritius credentials to open Payroll-GPT
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Sign in form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isSubmitting}
                autoComplete="current-password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting || !email || !password}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 rounded-lg border bg-muted/40 px-4 py-3 text-center text-sm">
            <p className="font-medium text-foreground">Don&apos;t have an account?</p>
            <p className="mt-1 text-muted-foreground">
              Register your company with{' '}
              <a
                href="https://payrollmauritius.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                payrollmauritius.com
              </a>{' '}
              to get access to Payroll-GPT.
            </p>
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            Secure access for authorised Payroll Mauritius users only
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
